import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider, Controller } from "react-hook-form";
import * as z from "zod";
import React, { useEffect } from "react";
import { useAuth } from "../../../lib/AuthContext";
import XanoAPI from "../../../lib/XanoAPI";
import { Button } from "../../ui/button";
import { toast } from "../../../components/ui/use-toast";
import { Input } from "../../ui/input";
import { FormItem, FormLabel, FormControl, FormMessage } from "../../ui/form";

const profileFormSchema = z.object({
  name: z
    .string()
    .min(2, {
      message: "Name must be at least 2 characters.",
    })
    .max(30, {
      message: "Name must not be longer than 30 characters.",
    }),
  email: z
    .string({
      required_error: "Email is required.",
    })
    .email("Please enter a valid email address."),
  agency_id: z.number(),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

export function ProfileForm() {
  const api = XanoAPI.getInstance();
  const auth = useAuth();
  const currentUser = auth.currentUser;

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: "",
      email: "",
      agency_id: 0,
    },
    mode: "onChange",
  });

  // Reset form when currentUser data is available
  useEffect(() => {
    if (currentUser) {
      console.log(
        "🔍 ProfileForm: Resetting form with currentUser data:",
        currentUser
      );
      form.reset({
        name: currentUser.name || "",
        email: currentUser.email || "",
        agency_id: currentUser.agency_id || 0,
      });
      console.log("🔍 ProfileForm: Form reset completed");
    } else {
      console.log("🔍 ProfileForm: currentUser is not available yet");
    }
  }, [currentUser, form]);

  const onSubmit = async (data: ProfileFormValues) => {
    if (!auth.currentUser || !auth.currentUser.id) {
      toast({
        title: "Error",
        description: "User ID is not available. Cannot update profile.",
      });
      return;
    }

    try {
      // Call the updateUser method
      await api.updateUser(auth.currentUser?.id.toString(), data);
      toast({
        title: "Profile Update Successful",
        description: "Your profile has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Profile Update Failed",
        description:
          "An error occurred while updating your profile. Please try again.",
      });
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Controller
          name="name"
          control={form.control}
          rules={{
            required: "Name is required.",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters long.",
            },
            maxLength: {
              value: 30,
              message: "Name must not be longer than 30 characters.",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <FormItem className="mb-6">
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormControl>
                <div className="flex">
                  <span className="flex justify-center items-center w-10 h-10 bg-gray-300 text-white rounded mr-1">
                    T
                  </span>
                  <Input
                    {...field}
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                  />
                </div>
              </FormControl>
              <FormMessage
                className={
                  error ? "text-red-500" : "text-gray-500 text-xs font-normal"
                }
              >
                {error ? error.message : "Enter your full name."}
              </FormMessage>
            </FormItem>
          )}
        />

        <Controller
          name="email"
          control={form.control}
          rules={{
            required: "Email is required.",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Please enter a valid email address.",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <FormItem className="mb-6">
              <FormLabel htmlFor="email">Email</FormLabel>
              <FormControl>
                <div className="flex">
                  <span className="flex justify-center items-center w-10 h-10 bg-gray-300 text-white rounded mr-1">
                    T
                  </span>
                  <Input
                    {...field}
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                  />
                </div>
              </FormControl>
              <FormMessage
                className={
                  error ? "text-red-500" : "text-gray-500 text-xs font-normal"
                }
              >
                {error ? error.message : "Enter your email address."}
              </FormMessage>
            </FormItem>
          )}
        />

        <Button type="submit" className="bg-accent">
          Update Profile
        </Button>
      </form>
    </FormProvider>
  );
}
